import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, Loader2, Upload, Sparkles } from 'lucide-react';

interface WizardAboutUsProps {
  onNext: () => void;
  siteId?: string;
}

type SiteType = 'agent' | 'broker' | 'community';

interface AboutUsData {
  site_type: SiteType;
  company_name: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  contact_city: string;
  contact_state: string;
  contact_zip: string;
  short_bio: string;
  long_bio: string;
  service_area_description: string;
}

const SITE_TYPE_OPTIONS = [
  { value: 'agent' as SiteType, label: 'Individual Agent', description: 'A site for a single real estate agent' },
  { value: 'broker' as SiteType, label: 'Broker / Team', description: 'A site for a brokerage or real estate team' },
  { value: 'community' as SiteType, label: 'Community Site', description: 'A general area guide without a specific agent' },
];

export function WizardAboutUs({ onNext, siteId }: WizardAboutUsProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [generatingShortBio, setGeneratingShortBio] = useState(false);
  const [generatingLongBio, setGeneratingLongBio] = useState(false);
  const [data, setData] = useState<AboutUsData>({
    site_type: 'agent',
    company_name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    contact_address: '',
    contact_city: '',
    contact_state: '',
    contact_zip: '',
    short_bio: '',
    long_bio: '',
    service_area_description: '',
  });

  useEffect(() => {
    if (siteId) {
      fetchData();
    }
  }, [siteId]);

  const fetchData = async () => {
    if (!siteId) return;

    try {
      // Fetch site data
      const { data: siteData } = await supabase
        .from('sites')
        .select('company_name, contact_name, contact_phone, contact_email, contact_address, contact_city, contact_state, contact_zip, tagline, description, location_served, ownership_type')
        .eq('id', siteId)
        .single();

      // Fetch realtor profile if exists
      const { data: profileData } = await supabase
        .from('realtor_profiles')
        .select('about_me, first_name, last_name, phone, address, city, state, zip')
        .eq('site_id', siteId)
        .maybeSingle();

      if (siteData) {
        // Use ownership_type with fallback to 'agent'
        const ownershipType = (siteData.ownership_type as SiteType) || 'agent';

        setData(prev => ({
          ...prev,
          site_type: ownershipType,
          company_name: siteData.company_name || '',
          contact_name: siteData.contact_name || (profileData ? `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() : ''),
          contact_phone: siteData.contact_phone || profileData?.phone || '',
          contact_email: siteData.contact_email || '',
          contact_address: siteData.contact_address || profileData?.address || '',
          contact_city: siteData.contact_city || profileData?.city || '',
          contact_state: siteData.contact_state || profileData?.state || '',
          contact_zip: siteData.contact_zip || profileData?.zip || '',
          short_bio: siteData.tagline || '',
          long_bio: profileData?.about_me || siteData.description || '',
          service_area_description: siteData.location_served || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching about us data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!siteId) return;

    setSaving(true);
    try {
      // Update site data - use ownership_type for agent/broker/community
      const { error: siteError } = await supabase
        .from('sites')
        .update({
          ownership_type: data.site_type,
          company_name: data.company_name,
          contact_name: data.contact_name,
          contact_phone: data.contact_phone,
          contact_email: data.contact_email,
          contact_address: data.contact_address,
          contact_city: data.contact_city,
          contact_state: data.contact_state,
          contact_zip: data.contact_zip,
          tagline: data.short_bio,
          description: data.long_bio,
          location_served: data.service_area_description,
        })
        .eq('id', siteId);

      if (siteError) throw siteError;

      // Update or create realtor profile
      const { data: existingProfile } = await supabase
        .from('realtor_profiles')
        .select('id')
        .eq('site_id', siteId)
        .maybeSingle();

      const nameParts = data.contact_name.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      if (existingProfile) {
        await supabase
          .from('realtor_profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            phone: data.contact_phone,
            address: data.contact_address,
            city: data.contact_city,
            state: data.contact_state,
            zip: data.contact_zip,
            about_me: data.long_bio,
          })
          .eq('id', existingProfile.id);
      } else {
        await supabase
          .from('realtor_profiles')
          .insert({
            site_id: siteId,
            first_name: firstName,
            last_name: lastName,
            phone: data.contact_phone,
            address: data.contact_address,
            city: data.contact_city,
            state: data.contact_state,
            zip: data.contact_zip,
            about_me: data.long_bio,
          });
      }

      toast({ title: 'About Us saved successfully' });
    } catch (error: any) {
      toast({
        title: 'Error saving',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const generateBio = async (type: 'short' | 'long') => {
    if (!data.contact_name && !data.company_name) {
      toast({
        title: 'Missing information',
        description: 'Please enter at least a business name or agent name first.',
        variant: 'destructive',
      });
      return;
    }

    const setGenerating = type === 'short' ? setGeneratingShortBio : setGeneratingLongBio;
    setGenerating(true);

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) throw new Error('Not authenticated');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-about-me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session.access_token}`,
        },
        body: JSON.stringify({
          siteId,
          bioType: type,
          context: {
            siteType: data.site_type,
            companyName: data.company_name,
            agentName: data.contact_name,
            city: data.contact_city,
            state: data.contact_state,
            serviceArea: data.service_area_description,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate bio');
      }

      const result = await response.json();
      
      if (type === 'short') {
        setData(prev => ({ ...prev, short_bio: result.bio }));
      } else {
        setData(prev => ({ ...prev, long_bio: result.bio }));
      }

      toast({ title: `${type === 'short' ? 'Short' : 'Full'} bio generated!` });
    } catch (error: any) {
      toast({
        title: 'Error generating bio',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveAndGenerate = async () => {
    await handleSave();
    
    setTriggering(true);
    try {
      // Create generation jobs for homepage, ads, and blog posts
      const jobs = [
        { site_id: siteId, job_type: 'homepage', status: 'pending' },
        { site_id: siteId, job_type: 'ads', status: 'pending' },
        { site_id: siteId, job_type: 'blog_posts', status: 'pending', total_items: 3 },
      ];

      const { error } = await supabase
        .from('generation_jobs')
        .insert(jobs);

      if (error) throw error;

      // Trigger the generation worker asynchronously
      const session = await supabase.auth.getSession();
      if (session.data.session) {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-generation-jobs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`,
          },
          body: JSON.stringify({}),
        }).catch(err => console.log('Generation worker triggered:', err));
      }

      toast({ 
        title: 'Generation started!',
        description: 'AI is now creating your homepage, ads, and blog posts.'
      });

      onNext();
    } catch (error: any) {
      toast({
        title: 'Error starting generation',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Site Type Selection */}
      <div className="space-y-3">
        <Label>What type of site is this?</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SITE_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setData(prev => ({ ...prev, site_type: option.value }))}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                data.site_type === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-muted hover:border-primary/50'
              }`}
            >
              <div className="font-medium">{option.label}</div>
              <div className="text-sm text-muted-foreground mt-1">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.site_type !== 'community' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="company_name">
                {data.site_type === 'broker' ? 'Brokerage / Team Name' : 'Company Name (optional)'}
              </Label>
              <Input
                id="company_name"
                value={data.company_name}
                onChange={(e) => setData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder={data.site_type === 'broker' ? 'ABC Realty Group' : 'Your Real Estate Company'}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_name">
                {data.site_type === 'broker' ? 'Team Lead / Primary Contact' : 'Agent Name'}
              </Label>
              <Input
                id="contact_name"
                value={data.contact_name}
                onChange={(e) => setData(prev => ({ ...prev, contact_name: e.target.value }))}
                placeholder={data.site_type === 'broker' ? 'Jane Smith' : 'John Smith'}
              />
            </div>
          </>
        )}

        {data.site_type === 'community' && (
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="company_name">Site Name</Label>
            <Input
              id="company_name"
              value={data.company_name}
              onChange={(e) => setData(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="Boston Real Estate Guide"
            />
            <p className="text-xs text-muted-foreground">
              This is a community site without a specific agent or broker.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="contact_phone">Phone</Label>
          <Input
            id="contact_phone"
            value={data.contact_phone}
            onChange={(e) => setData(prev => ({ ...prev, contact_phone: e.target.value }))}
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="contact_email">Email</Label>
          <Input
            id="contact_email"
            type="email"
            value={data.contact_email}
            onChange={(e) => setData(prev => ({ ...prev, contact_email: e.target.value }))}
            placeholder="contact@example.com"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="contact_address">Office Address</Label>
        <Input
          id="contact_address"
          value={data.contact_address}
          onChange={(e) => setData(prev => ({ ...prev, contact_address: e.target.value }))}
          placeholder="123 Main Street"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="contact_city">City</Label>
          <Input
            id="contact_city"
            value={data.contact_city}
            onChange={(e) => setData(prev => ({ ...prev, contact_city: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contact_state">State</Label>
          <Input
            id="contact_state"
            value={data.contact_state}
            onChange={(e) => setData(prev => ({ ...prev, contact_state: e.target.value }))}
          />
        </div>
        <div className="space-y-2 col-span-2 md:col-span-2">
          <Label htmlFor="contact_zip">ZIP Code</Label>
          <Input
            id="contact_zip"
            value={data.contact_zip}
            onChange={(e) => setData(prev => ({ ...prev, contact_zip: e.target.value }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="service_area_description">Service Area / Market</Label>
        <Input
          id="service_area_description"
          value={data.service_area_description}
          onChange={(e) => setData(prev => ({ ...prev, service_area_description: e.target.value }))}
          placeholder="e.g. Boston, Massachusetts or Greater Boston Area"
        />
        <p className="text-xs text-muted-foreground">This is used by AI when generating your bio and content.</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="short_bio">Short Bio (for snippets and previews)</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => generateBio('short')}
            disabled={generatingShortBio}
            className="h-7 text-xs"
          >
            {generatingShortBio ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            Generate with AI
          </Button>
        </div>
        <Textarea
          id="short_bio"
          value={data.short_bio}
          onChange={(e) => setData(prev => ({ ...prev, short_bio: e.target.value }))}
          placeholder="A brief 1-2 sentence description of your services..."
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="long_bio">Full Bio / About Us</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => generateBio('long')}
            disabled={generatingLongBio}
            className="h-7 text-xs"
          >
            {generatingLongBio ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            Generate with AI
          </Button>
        </div>
        <Textarea
          id="long_bio"
          value={data.long_bio}
          onChange={(e) => setData(prev => ({ ...prev, long_bio: e.target.value }))}
          placeholder="Tell potential clients about your experience, expertise, and what sets you apart..."
          rows={5}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Progress
        </Button>

        <Button onClick={handleSaveAndGenerate} disabled={saving || triggering}>
          {triggering ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Starting Generation...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Save & Generate Content
              <ChevronRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
